import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { LocaleService } from '@next/core-lib/i18n';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from '../../../shared/models';
import { InputMethodEnum, ValueRegistrationEnum } from '../../models/flock.model';
import { FlockWeekregistrationKpiService } from '../../services/flock-weekregistration-kpi.service';
import { getDeliveryLines } from '../../store/selectors/flock.selectors';

@Component({
  selector: 'app-week-registration-form',
  templateUrl: './week-registration-form.component.html',
  standalone: false,
})
export class WeekRegistrationFormComponent implements OnInit, OnDestroy {
  @Input() formGroup: FormGroup;
  @Input() showFields: string[];
  @Input() inputMethod: InputMethodEnum;
  @Input() amountOfHens: number;
  @Input() selectedForageProductsAndMoult: string[];
  @Input() index: number;
  @Input() isEditMode?: boolean;
  @Output() mortalityChangedEvent = new EventEmitter<[number, number]>();
  @Output() isInMoultChangedEvent = new EventEmitter<[number, boolean]>();
  @Output() isSecondForageChangedEvent = new EventEmitter<[number, boolean]>();
  @Output() forageProductChangedEvent = new EventEmitter<
    [number, { productName: string; isSecondProduct: boolean }]
  >();

  public valueRegistrationTypes: typeof ValueRegistrationEnum = ValueRegistrationEnum;
  forageProducts: string[] = [];
  showSecondForage = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private kpiService: FlockWeekregistrationKpiService,
    private locale: LocaleService,
  ) {
    this.store
      .select(getDeliveryLines)
      .pipe(takeUntil(this.destroy$))
      .subscribe((deliverylines) => {
        this.forageProducts = (deliverylines ?? [])
          .filter((line) => line.isActive)
          .map((line) => `${line.externalProductId} - ${line.productTitle}`)
          .filter((item, i, array) => array.indexOf(item) === i);
      });
  }

  ngOnInit() {
    this.setForageProductSelection();
    this.showSecondForage = this.formGroup.controls.isSecondForage.value;

    if (this.selectedForageProductsAndMoult) {
      this.formGroup.controls.isInMoult.setValue(this.selectedForageProductsAndMoult[2] === 'true');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setForageProductSelection() {
    // if no forageProducts are set in the weekreg
    // they should be populated with the forageProducts of the previous week
    // unless we are in the edit mode so the saved value is not overriden
    if (!!this.selectedForageProductsAndMoult && !this.isEditMode) {
      if (
        this.formGroup.controls.forageProduct.value === null &&
        this.forageProducts.some((p) => p === this.selectedForageProductsAndMoult[0])
      ) {
        this.formGroup.controls.forageProduct.setValue(this.selectedForageProductsAndMoult[0]);
      }
    }
  }

  showField(fieldToShow: string): boolean {
    return !!this.showFields && this.showFields.findIndex((field) => field === fieldToShow) > -1;
  }

  getValidationErrorMessage(field: ValueRegistrationEnum): string {
    const ranges = this.kpiService.getValueRegistrationRanges(
      field,
      this.inputMethod,
      this.amountOfHens,
    );
    return `${this.locale.translate('week-registration.out-of-range')} ${ranges[0]} - ${ranges[1]}`;
  }

  onForageProductChange(changeEvent: MatSelectChange, isSecondProduct: boolean) {
    if (this.inputMethod === InputMethodEnum.PerDay) {
      const event: [number, { productName: string; isSecondProduct: boolean }] = [
        this.index,
        { productName: changeEvent.value as string, isSecondProduct },
      ];
      this.forageProductChangedEvent.emit(event);
    }
  }

  onIsSecondForageChange(changeEvent: MatSlideToggleChange) {
    this.showSecondForage = !this.showSecondForage;

    if (this.inputMethod === InputMethodEnum.PerDay) {
      this.isSecondForageChangedEvent.emit([this.index, changeEvent.checked]);
    }
  }

  onIsInMoultChange(changeEvent: MatSlideToggleChange) {
    if (this.inputMethod === InputMethodEnum.PerDay) {
      this.isInMoultChangedEvent.emit([this.index, changeEvent.checked]);
    }
  }

  onMortalityChange() {
    // Mortality change updates are only applicable for mortality that is registered on a daily basis.
    if (this.inputMethod === InputMethodEnum.PerDay) {
      const event: [number, number] = [
        this.index,
        this.formGroup.controls.mortality.value as number,
      ];
      this.mortalityChangedEvent.emit(event);
    }
  }
}
